import { jsx as _jsx } from "react/jsx-runtime";
import { noop } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PERF_MARK_PREFIX } from 'performanceObserver/measure';
import { logger } from 'services';
const WHITESPACE_RE = /\s+/;
let logrocketModulePromise = null;
async function getLogrocketSessionURL() {
    var _ref;
    var _m_default;
    logrocketModulePromise !== null && logrocketModulePromise !== void 0 ? logrocketModulePromise : logrocketModulePromise = import(/* webpackChunkName: "logrocket" */ 'logrocket').catch((err)=>{
        logrocketModulePromise = null;
        throw err;
    });
    const m = await logrocketModulePromise;
    return (_ref = (_m_default = m.default) === null || _m_default === void 0 ? void 0 : _m_default.sessionURL) !== null && _ref !== void 0 ? _ref : undefined;
}
function useLog() {
    return useCallback((event, details)=>{
        (async ()=>{
            let extra = {};
            try {
                const sessionURL = await getLogrocketSessionURL();
                if (sessionURL) {
                    extra = {
                        logrocketSessionUrl: `${sessionURL}`
                    };
                }
            } catch (unused) {
            // LogRocket chunk unavailable — log without session URL
            }
            const logData = {
                type: event,
                account: window.location.host,
                url: window.location.pathname,
                ...'connection' in navigator ? (()=>{
                    const conn = navigator.connection;
                    return {
                        connectionInfo: `rtt: ${conn.rtt}, downlink: ${conn.downlink}, effectiveType: ${conn.effectiveType}, saveData: ${conn.saveData}`
                    };
                })() : {},
                ...extra,
                ...details
            };
            logger.log(`Rl perf event - ${event}`, logData);
        })().catch(()=>{
        // best-effort — logging failures must not propagate as unhandled rejections
        });
    }, []);
}
const PerfObserverContext = /*#__PURE__*/ createContext({
    setInpConfig: (_config)=>undefined
});
export const usePerfObserver = ()=>{
    return useContext(PerfObserverContext);
};
/**
 * Observes all Performance API measure entries whose name starts with PERF_MARK_PREFIX,
 * strips the prefix, and forwards them to Datadog via logger.log with enriched context
 * (account, URL, connection info, LogRocket session URL for correlation).
 * Set up once on mount and disconnected on unmount.
 */ export const PerfObserver = ({ children })=>{
    const log = useLog();
    useEffect(()=>{
        if (!('PerformanceObserver' in window)) {
            return noop;
        }
        const observer = new PerformanceObserver((list)=>{
            list.getEntries().filter((entry)=>entry.name.startsWith(PERF_MARK_PREFIX)).forEach((entry)=>{
                const eventName = entry.name.slice(PERF_MARK_PREFIX.length);
                const measure = entry;
                const { detail } = measure;
                const safeDetail = detail !== null && typeof detail === 'object' && !Array.isArray(detail) ? detail : {};
                try {
                    log(eventName, {
                        duration: Math.floor(measure.duration),
                        ...safeDetail
                    });
                } catch (unused) {
                // ignore errors
                }
            });
        });
        try {
            observer.observe({
                type: 'measure',
                buffered: true
            });
        } catch (unused) {
            return noop;
        }
        return ()=>{
            try {
                observer.disconnect();
            } catch (unused) {
            // best-effort
            }
        };
    }, [
        log
    ]);
    // INP: observe raw interaction events (click, keydown).
    // durationThreshold=40 matches the Web Vitals library minimum — interactions
    // faster than 40ms are imperceptible and not worth logging.
    // The three INP components are:
    //   inputDelay        = time waiting for the main thread (processingStart - startTime)
    //   processingTime    = time spent in JS event handlers (processingEnd - processingStart)
    //   presentationDelay = time from handler finish to next paint (startTime + duration - processingEnd)
    const [inpConfig, setInpConfig] = useState({
        capture: false,
        label: '',
        boundary: '',
        traits: {}
    });
    const inpEntriesRef = useRef([]);
    useEffect(()=>{
        if (!('PerformanceObserver' in window)) {
            return noop;
        }
        let boundaryEl = null;
        if (inpConfig.boundary) {
            try {
                boundaryEl = document.querySelector(inpConfig.boundary);
            } catch (unused) {
            // Invalid CSS selector — treat as no boundary.
            }
        }
        const observer = new PerformanceObserver((list)=>{
            list.getEntries().filter((entry)=>entry.name === 'click' || entry.name === 'keydown').filter((entry)=>{
                if (!boundaryEl) {
                    return true;
                }
                try {
                    const { target } = entry;
                    return target instanceof Node && boundaryEl.contains(target);
                } catch (unused) {
                    return false;
                }
            }).forEach((entry)=>{
                try {
                    var _ref, _ref1, _el_getAttribute;
                    const e = entry;
                    const el = e.target instanceof Element ? e.target : null;
                    // SVGElement.className is SVGAnimatedString (not a string), so calling
                    // .trim() on it would throw. Use getAttribute('class') for a plain string
                    // on all element types.
                    const classStr = el instanceof HTMLElement ? el.className : (_ref = el === null || el === void 0 ? void 0 : el.getAttribute('class')) !== null && _ref !== void 0 ? _ref : '';
                    const targetDescription = el ? (_ref1 = (_el_getAttribute = el.getAttribute('data-cy')) !== null && _el_getAttribute !== void 0 ? _el_getAttribute : el.id || null) !== null && _ref1 !== void 0 ? _ref1 : classStr ? `${el.tagName.toLowerCase()}.${classStr.trim().split(WHITESPACE_RE).join('.')}` : el.tagName.toLowerCase() : '';
                    inpEntriesRef.current.push({
                        eventType: e.name,
                        duration: Math.floor(e.duration),
                        inputDelay: Math.floor(e.processingStart - e.startTime),
                        processingTime: Math.floor(e.processingEnd - e.processingStart),
                        presentationDelay: Math.floor(e.startTime + e.duration - e.processingEnd),
                        targetDescription
                    });
                    if (inpEntriesRef.current.length > 500) {
                        inpEntriesRef.current.shift();
                    }
                } catch (unused) {
                // Don't let entry processing crash the observer.
                }
            });
        });
        if (inpConfig.capture) {
            try {
                // durationThreshold is valid per spec but missing from older TS DOM typings.
                observer.observe({
                    type: 'event',
                    buffered: true,
                    durationThreshold: 40
                });
            } catch (unused) {
                // 'event' entry type unsupported in this browser.
                return noop;
            }
            return ()=>{
                try {
                    observer.disconnect();
                } catch (unused) {
                // best-effort — spec says disconnect() must not throw, but guard against browser bugs
                }
                try {
                    const entries = inpEntriesRef.current;
                    inpEntriesRef.current = [];
                    if (entries.length === 0) {
                        return;
                    }
                    // Nearest-rank p90: Math.ceil(n * 0.9) - 1 gives the correct index so
                    // that for 10 entries p90 is index 8 (9th element), not index 9 (the max).
                    const p90Of = (nums)=>{
                        const s = [
                            ...nums
                        ].sort((a, b)=>a - b);
                        return s[Math.max(0, Math.ceil(s.length * 0.9) - 1)];
                    };
                    const byType = entries.reduce((acc, e)=>{
                        var _acc, _e_eventType, _;
                        ((_ = (_acc = acc)[_e_eventType = e.eventType]) !== null && _ !== void 0 ? _ : _acc[_e_eventType] = []).push(e);
                        return acc;
                    }, {});
                    Object.entries(byType).forEach(([eventType, group])=>{
                        try {
                            var _inpConfig_traits;
                            const sortedByDuration = [
                                ...group
                            ].sort((a, b)=>a.duration - b.duration);
                            const worst = sortedByDuration[sortedByDuration.length - 1];
                            const p90Entry = sortedByDuration[Math.max(0, Math.ceil(sortedByDuration.length * 0.9) - 1)];
                            log('INP_BATCH', {
                                eventType,
                                count: group.length,
                                p90Duration: p90Of(group.map((e)=>e.duration)),
                                p90InputDelay: p90Of(group.map((e)=>e.inputDelay)),
                                p90ProcessingTime: p90Of(group.map((e)=>e.processingTime)),
                                p90PresentationDelay: p90Of(group.map((e)=>e.presentationDelay)),
                                worstDuration: worst.duration,
                                worstInputDelay: worst.inputDelay,
                                worstProcessingTime: worst.processingTime,
                                worstPresentationDelay: worst.presentationDelay,
                                ...(p90Entry === null || p90Entry === void 0 ? void 0 : p90Entry.targetDescription) ? {
                                    p90Target: p90Entry.targetDescription
                                } : {},
                                ...worst.targetDescription ? {
                                    worstTarget: worst.targetDescription
                                } : {},
                                ...inpConfig.label ? {
                                    label: inpConfig.label
                                } : {},
                                ...(_inpConfig_traits = inpConfig.traits) !== null && _inpConfig_traits !== void 0 ? _inpConfig_traits : {}
                            });
                        } catch (unused) {
                        // don't let one event type's failure drop the rest
                        }
                    });
                } catch (unused) {
                // Don't let entry processing crash the observer.
                }
            };
        }
        return noop;
    }, [
        log,
        inpConfig.capture,
        inpConfig.label,
        inpConfig.boundary,
        inpConfig.traits
    ]);
    const contextValue = useMemo(()=>{
        return {
            setInpConfig: (config)=>setInpConfig((prev)=>({
                        ...prev,
                        ...config
                    }))
        };
    }, []);
    return /*#__PURE__*/ _jsx(PerfObserverContext.Provider, {
        value: contextValue,
        children: children
    });
};
